import React, { useEffect, useState } from "react";
import { Copy, Check, KeyRound, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import useRequest from "../../../components/hook/use-req";
import { ApiKey, ApiKeysListResponse, formatOrgDate } from "./types";

const KEY_TYPE_STYLES: Record<string, string> = {
  live: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400",
  test: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

// Shown once, right after creation — the backend never returns the full
// plain_key again after this response, so the only chance to copy it is now.
const NewKeyRevealModal: React.FC<{ apiKey: ApiKey | null; onClose: () => void }> = ({ apiKey, onClose }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => setCopied(false), [apiKey]);

  if (!apiKey) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey.plain_key || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select and copy the key manually.");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center shrink-0">
              <KeyRound size={16} className="text-amber-600 dark:text-amber-400" />
            </span>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Copy your API key now</h2>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This is the only time the full key is shown. Store it securely — you won't be able to see it again.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 px-3 py-2.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100 break-all">
              {apiKey.plain_key}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              title="Copy"
            >
              {copied ? <Check size={15} className="text-green-600 dark:text-green-400" /> : <Copy size={15} />}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-end px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateKeyModal: React.FC<{ isOpen: boolean; onClose: () => void; orgId: string; onCreated: (key: ApiKey) => void }> = ({
  isOpen,
  onClose,
  orgId,
  onCreated,
}) => {
  const token = localStorage.getItem("authToken");
  const [name, setName] = useState("");
  const [keyType, setKeyType] = useState<"live" | "test">("live");
  const [submitting, setSubmitting] = useState(false);

  const { makeRequest: createKey } = useRequest("/admin/organizations", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const handleClose = () => {
    if (submitting) return;
    setName("");
    setKeyType("live");
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Give this key a name.");
      return;
    }
    setSubmitting(true);
    const [response, status] = (await createKey({ name: name.trim(), key_type: keyType }, undefined, `${orgId}/api-keys`)) as [ApiKey, number];
    setSubmitting(false);
    if (status === 201 || (status >= 200 && status < 300)) {
      setName("");
      setKeyType("live");
      onCreated(response);
      onClose();
    } else {
      toast.error((response as any)?.detail || (response as any)?.message || "Failed to create API key.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
              <KeyRound size={16} className="text-blue-600 dark:text-blue-400" />
            </span>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Create API Key</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Production Key"
              className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Key Type</label>
            <div className="flex gap-2">
              {(["live", "test"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setKeyType(type)}
                  className={`flex-1 px-3 py-2.5 text-sm font-medium rounded-lg border capitalize transition-colors ${
                    keyType === type
                      ? "border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500"
                      : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
          >
            {submitting ? "Creating…" : "Create Key"}
          </button>
        </div>
      </div>
    </div>
  );
};

// "API Keys" tab inside the Business detail drawer — GET/POST/DELETE
// /admin/organizations/{org_id}/api-keys. Its own self-contained fetch
// rather than piggybacking on OrgDetailResponse, since the parent drawer
// only loads it when this tab is actually opened.
const ApiKeysTab: React.FC<{ orgId: string }> = ({ orgId }) => {
  const token = localStorage.getItem("authToken");
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<"" | "live" | "test">("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [revealKey, setRevealKey] = useState<ApiKey | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const { makeRequest: getKeys } = useRequest("/admin/organizations", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: deleteKey } = useRequest("/admin/organizations", "DELETE", {
    Authorization: `Bearer ${token}`,
  });

  const fetchKeys = async () => {
    setLoading(true);
    const params: Record<string, unknown> = {};
    if (typeFilter) params.key_type = typeFilter;
    const [response] = (await getKeys(undefined, params, `${orgId}/api-keys`)) as [ApiKeysListResponse, number];
    setKeys(Array.isArray(response?.keys) ? response.keys : []);
    setLoading(false);
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, typeFilter]);

  const handleCreated = (key: ApiKey) => {
    fetchKeys();
    if (key?.plain_key) setRevealKey(key);
  };

  const handleRevoke = async (key: ApiKey) => {
    if (!window.confirm(`Revoke "${key.name}"? Anything using this key will stop working immediately.`)) return;
    setRevokingId(key.id);
    const [response, status] = await deleteKey(undefined, undefined, `${orgId}/api-keys/${key.id}`);
    setRevokingId(null);
    if (status >= 200 && status < 300 && response?.success !== false) {
      toast.success(response?.message || "API key revoked.");
      setKeys((prev) => (prev ? prev.map((k) => (k.id === key.id ? { ...k, is_active: false } : k)) : prev));
    } else {
      toast.error(response?.detail || response?.message || "Failed to revoke API key.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex gap-1.5">
          {(["", "live", "test"] as const).map((type) => (
            <button
              key={type || "all"}
              type="button"
              onClick={() => setTypeFilter(type)}
              className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize transition-colors ${
                typeFilter === type
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {type || "All"}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
        >
          <Plus size={13} /> Create Key
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !keys || keys.length === 0 ? (
        <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No API keys yet.</p>
      ) : (
        <div className="border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  {["Name", "Type", "Key", "Status", "Last Used", "Created", ""].map((h) => (
                    <th key={h} className="px-3 py-2 text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id} className="border-b border-gray-50 dark:border-gray-800/60 last:border-b-0">
                    <td className="px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 truncate max-w-[140px]">{key.name}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full uppercase ${KEY_TYPE_STYLES[key.key_type] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"}`}>
                        {key.key_type}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs font-mono text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {key.key_prefix}••••{key.last_four}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                          key.is_active
                            ? "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400"
                            : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                        }`}
                      >
                        {key.is_active ? "active" : "revoked"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">{formatOrgDate(key.last_used_at)}</td>
                    <td className="px-3 py-2.5 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">{formatOrgDate(key.created_at)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {key.is_active && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(key)}
                          disabled={revokingId === key.id}
                          title="Revoke key"
                          className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-50 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CreateKeyModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} orgId={orgId} onCreated={handleCreated} />
      <NewKeyRevealModal apiKey={revealKey} onClose={() => setRevealKey(null)} />
    </div>
  );
};

export default ApiKeysTab;
